import * as React from 'react';
import { useParams } from 'react-router-dom';
import { PDFExport } from '@progress/kendo-react-pdf';
import { StackLayout, Avatar } from '@progress/kendo-react-layout';

import { Downloadble } from '../../downloadable';
import {
  EventReportLeaderboardItem,
  EventReportLeaderboardResponse
} from '../../../../types/responses/event-report-response';
import { useGetLeaderboardData } from '../../../../hooks/services/use-reports';

const ReportLeaderboardItem: React.FC<{
  rank: number;
  name: string;
  score: number;
  avatar?: string;
}> = ({ avatar, name, rank, score }) => {
  return (
    <div className={'p-2 border-bottom'}>
      <div className={'d-flex container justify-content-center'}>
        <div className={'d-flex align-items-center'}>
          <h2 className={'text-black fw-normal'}>{`${rank}.`}</h2>
        </div>
        <div className={'col-2 justify-content-start align-items-center'}>
          {avatar && (
            <Avatar
              type={'image'}
              themeColor={'light'}
              className={`h-3 w-3 border border-5 p-2 border-success`}>
              <img
                src={avatar}
                className={`h-3 w-3 rounded-circle`}
                alt={'Player Avatar'}
              />
            </Avatar>
          )}
        </div>
        <div className={`${'col-3'} d-flex align-items-center`}>
          <h2 className={'text-black fw-normal'}>{name}</h2>
        </div>
        <div className={'col-3 d-flex justify-content-end align-items-center'}>
          <h2 className={'text-right text-black fw-normal'}>{score}</h2>
        </div>
      </div>
    </div>
  );
};

const LeaderboardSection: React.FC<{
  title: string;
  items: EventReportLeaderboardItem[];
  pageBreakBefore?: boolean;
}> = ({ title, items, pageBreakBefore }) => {
  return (
    <div className={`${pageBreakBefore ? 'page-break' : ''} text-center mb-8`}>
      <h1 className={'text-primary'}>{title}</h1>
      <hr />

      <StackLayout orientation={'vertical'} className={'w-100'}>
        {[...items]
          .sort((a, b) => b.score - a.score)
          .map((item, index) => (
            <ReportLeaderboardItem
              key={`${item.name}-${index}`}
              name={item.name}
              rank={index + 1}
              score={item.score}
              avatar={item.avatar}
            />
          ))}
      </StackLayout>
    </div>
  );
};

export const DownloadableLeaderboard: React.FC<{ fileName: string }> = ({
  fileName
}) => {
  const { eventId, detailId } = useParams();

  const { isMutating, trigger } = useGetLeaderboardData();
  const [reportData, setReportData] =
    React.useState<EventReportLeaderboardResponse | null>(null);
  const [isExporting, setIsExporting] = React.useState(false);

  const pdfExportRef = React.useRef<PDFExport | null>(null);

  const onClick = async () => {
    if (!eventId || !detailId) return;

    try {
      const response = await trigger({ eventId, playlistId: detailId });
      setReportData(response);
      setIsExporting(true);
    } catch (err) {
      console.error(err);
    }
  };

  React.useEffect(() => {
    if (!isExporting || !reportData) return;

    const id = requestAnimationFrame(() => {
      pdfExportRef.current?.save();
      setIsExporting(false);
    });

    return () => cancelAnimationFrame(id);
  }, [isExporting, reportData]);

  const hasTeams = !!reportData?.teams?.length;
  const isLoading = isMutating || isExporting;

  return (
    <>
      <Downloadble
        text={'Leaderboard'}
        icon={'123'}
        bgColor={'#FEB333'}
        disabled={isLoading}
        downloadText={isLoading ? 'Downloading' : 'Download'}
        onClick={onClick}
      />

      {/* hidden leaderboard content */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        {reportData && (
          <PDFExport
            ref={pdfExportRef}
            paperSize={'A4'}
            scale={0.6}
            margin={20}
            forcePageBreak={'.page-break'}
            fileName={`${fileName}-Leaderboard.pdf`}>
            <LeaderboardSection
              title={'Player Leaderboard'}
              items={reportData.players}
            />
            {hasTeams && (
              <LeaderboardSection
                title={'Team Leaderboard'}
                items={reportData.teams}
                pageBreakBefore
              />
            )}
          </PDFExport>
        )}
      </div>
    </>
  );
};
